import React, { useEffect, useState, useRef } from 'react';
import { NavLink } from "react-router-dom";
import { Header } from '../../Components/Layout/Header.jsx';
import { ObavijestiCasino } from '../../Components/Layout/ObavijestiCasino.jsx';
import { MetaTags } from '../../Components/Layout/MetaTags.jsx';
import RiveComponent from "../../Components/Shared/RiveComponent";

export default function SuperStory(props) {
    // ========== STATE VARIABLES ==========
    // Index of the currently active story
    const [activeIndex, setActiveIndex] = useState(0);
    // Video progress (value between 0 and 1)
    const [videoProgress, setVideoProgress] = useState(0);
    // Whether the current video is playing
    const [isPlaying, setIsPlaying] = useState(false);
    // Whether the screen is in landscape orientation
    const [isLandscape, setIsLandscape] = useState(false);
    // The actual width of the video being displayed
    const [videoWidth, setVideoWidth] = useState(null);
    // Screen height to adapt video size
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);
    // Story data state
    const [storyData, setStoryData] = useState([]);
    // State for rendering no more stories container with timeout
    const [showNoMoreStories, setShowNoMoreStories] = useState(false);

    // ========== REFS ==========
    // Holds references to each video element
    const videoRefs = useRef([]);

    // ========== LIFECYCLE FUNCTIONS ==========
    // ========== EFFECT: On Mount ==========
    useEffect(() => {
        fetchData();

        // Save original background color from body, padding and overflow from #app
        const originalBg = document.body.style.backgroundColor;
        const originalPadding = document.querySelector("#app").style.paddingBottom;
        const originalOverflow = document.querySelector("#app").style.overflow;

        // Set body background color to black and for #app paddingBottom, overflow, position and height to new values
        document.body.style.backgroundColor = 'black';
        document.querySelector("#app").style.paddingBottom = '0';
        document.querySelector("#app").style.overflow = 'visible';
        document.querySelector("#app").style.position = 'absolute';
        document.querySelector("#app").style.height = '100%';

        // Preload all videos
        videoRefs.current.forEach((video) => {
            if (video) video.load(); // Triggers preloading
        });

        // Restore on unmount (cleanup)
        return () => {
            document.body.style.backgroundColor = originalBg || '';
            document.querySelector("#app").style.paddingBottom = originalPadding || '';
            document.querySelector("#app").style.overflow = originalOverflow || '';
            document.querySelector("#app").style.position = '';
            document.querySelector("#app").style.height = '';
        };
    }, []);


    const fetchData = () => {
        props.showLoadingFn(true);
        var promises = Promise.all([casino.repos.notifikacijeRepo.list(false, true)]);
        var dataPromises = casino.utils.makeCancelablePromise(promises);
        dataPromises.promise.then(
            (data) => {
                const stories = data?.[0]?.story;
                setStoryData(Array.isArray(stories) ? stories : []);
            },
            (reason) => {
                console.warn("Promise rejected:", reason);
                setStoryData([]);
            }
        );
    }

    // ========== EFFECT: Watch activeIndex (video playback setup) ==========
    useEffect(() => {
        // delay div no-more-stories if storyData is empty
        if (storyData.length === 0) {
            const timeout = setTimeout(() => {
                setShowNoMoreStories(true);
            }, 1000);

            return () => clearTimeout(timeout);
        } else {
            // Reset if data appears
            setShowNoMoreStories(false);
        }

        if (!storyData.length) return;

        const timeout = setTimeout(() => {
            const video = videoRefs.current[activeIndex];
            if (!video) return;

            let requestId = null;
            let isCancelled = false;

            // Continuously update video progress using requestAnimationFrame
            const updateProgress = () => {
                if (isCancelled) return;
                if (video.duration > 0) {
                    setVideoProgress(video.currentTime / video.duration);
                }
                requestId = requestAnimationFrame(updateProgress);
            };

            // Handlers for video buffering and playing states
            const handleWaiting = () => setIsPlaying(false);
            const handlePlaying = () => setIsPlaying(true);
            const handleCanPlay = () => setIsPlaying(true);

            // Start video and calculate visible width
            const onLoadedMetadata = () => {
                if (isCancelled) return;
                video.currentTime = 0;
                if (!isLandscape) {
                    video.play().catch((err) => console.warn('Play error:', err));
                }
                updateProgress();

                const containerWidth = video.clientWidth;
                const containerHeight = video.clientHeight;
                const videoAspect = video.videoWidth / video.videoHeight;
                const containerAspect = containerWidth / containerHeight;

                let visibleVideoWidth;
                if (videoAspect > containerAspect) {
                    // Video is wider than container – full width, height is reduced
                    visibleVideoWidth = containerWidth;
                } else {
                    // Video is taller than container – full height, width is reduced
                    visibleVideoWidth = containerHeight * videoAspect;
                }

                setVideoWidth(visibleVideoWidth);
            };

            // Attach relevant event listeners, either already loaded or wait
            if (!isNaN(video.duration)) {
                onLoadedMetadata();
            } else {
                video.addEventListener('loadedmetadata', onLoadedMetadata);
            }

            video.addEventListener('waiting', handleWaiting);
            video.addEventListener('playing', handlePlaying);
            video.addEventListener('canplay', handleCanPlay);

            // ResizeObserver, handles dynamic resizing of video
            const observer = new ResizeObserver(() => {
                const containerWidth = video.clientWidth;
                const containerHeight = video.clientHeight;
                const videoAspect = video.videoWidth / video.videoHeight;
                const containerAspect = containerWidth / containerHeight;

                let visibleVideoWidth;
                if (videoAspect > containerAspect) {
                    visibleVideoWidth = containerWidth;
                } else {
                    visibleVideoWidth = containerHeight * videoAspect;
                }

                setVideoWidth(visibleVideoWidth);
            });

            observer.observe(video);

            // Cleanup listeners and animation frames
            return () => {
                isCancelled = true;
                cancelAnimationFrame(requestId);
                video.removeEventListener('waiting', handleWaiting);
                video.removeEventListener('playing', handlePlaying);
                video.removeEventListener('canplay', handleCanPlay);
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                observer.disconnect();
            };
        }, 0);

        return () => clearTimeout(timeout);
    }, [activeIndex, storyData]);

    // ========== EFFECT: Orientation detection ==========
    useEffect(() => {
        const checkOrientation = () => {
            setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
            setScreenHeight(window.innerHeight);
        };

        checkOrientation(); // check initially

        // Listen to orientation or resize changes
        window.addEventListener("resize", checkOrientation);
        window.addEventListener("orientationchange", checkOrientation);

        return () => {
            window.removeEventListener("resize", checkOrientation);
            window.removeEventListener("orientationchange", checkOrientation);
        };
    }, []);

    useEffect(() => {
        if (isLandscape && videoRefs.current[activeIndex]) {
            videoRefs.current[activeIndex].pause();
        } else if (!isLandscape && videoRefs.current[activeIndex]) {
            videoRefs.current[activeIndex].play();
        }
    }, [isLandscape]);

    // ========== VIDEO EVENTS ==========
    const handleVideoStart = () => {
        //Set story as read
        if (props.user && storyData[activeIndex])
            casino.repos.notifikacijeRepo.remove(storyData[activeIndex]);
    }

    const handleVideoEnd = () => {
        if (activeIndex < storyData.length - 1) {
            setVideoProgress(0);
            setActiveIndex((prev) => prev + 1);
        }
        // Optional: close or loop if last story
        // else do nothing, or reset to 0 if you want loop
    };

    // ========== NAVIGATION CONTROLS ==========
    const goToPrevious = () => {
        const currentVideo = videoRefs.current[activeIndex];
        if (!currentVideo) return;

        // Pause and reset the current video
        currentVideo.pause();
        currentVideo.currentTime = 0;

        if (activeIndex === 0) {
            setVideoProgress(0);
            currentVideo.play();
        } else {
            setVideoProgress(0);
            setActiveIndex((prev) => prev - 1);
        }
    };

    const goToNext = () => {
        const currentVideo = videoRefs.current[activeIndex];
        if (!currentVideo) return;

        // If video is not started due to autoplay policy (https://goo.gl/xX8pDD)
        // do not go to next, but start current video first.
        if (!activeIndex && currentVideo.paused && !currentVideo.currentTime) {
            currentVideo.play();
            return;
        }

        // Pause and reset the current video
        currentVideo.pause();
        currentVideo.currentTime = 0;

        if (activeIndex === storyData.length - 1) {
            // End last video
            setVideoProgress(1);
            currentVideo.currentTime = currentVideo.duration || currentVideo.seekable.end(0);
        } else {
            setVideoProgress(0);
            setActiveIndex((prev) => prev + 1);
        }
    };

    // ========== RENDER INDIVIDUAL STORY ==========
    const renderStory = (story, index) => {
        const isActive = index === activeIndex;

        return (
            <div
                key={story.igracNotifikacijaID}
                className={`story-container story-container--${story.igracNotifikacijaID}`}
                style={{
                    height: isLandscape ? '100%' : 'auto',
                    maxHeight: isLandscape ? '100%' : `${screenHeight}px`,
                    zIndex: isActive ? 1 : 0,
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? 'auto' : 'none',
                }}
            >
                <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={story.videoUrl}
                    preload="auto"
                    playsInline
                    className="story-video"
                    style={{ height: `${screenHeight + 50}px`, maxHeight: `${screenHeight + 50}px` }}
                    onPlay={handleVideoStart}
                    onEnded={handleVideoEnd}
                />
                <div className="bottom-container" style={{ width: videoWidth ? `${videoWidth}px` : '100%', height: isLandscape ? '40%' : '20%', bottom: isLandscape ? '-10px' : '50px' }}>
                    <div className="story-info-container">
                        <div
                            className="story-title"
                            style={{ color: `#FFFFFF` }}
                        >
                            {story.naslov}
                        </div>
                        <div
                            className={"story-info-text " + (isLandscape ? "text-ellipsis" : "")}
                            style={{ color: `#FFFFFF` }}
                        >
                            {story.pushText}
                        </div>
                    </div>
                    <div className="story-btns">
                        <NavLink
                            className="close-story-btn"
                            style={{ color: `#FFFFFF` }}
                            to={props.routePrefix || '' + '/'}
                            type="button"
                        >
                            ZATVORI
                        </NavLink>
                        <a
                            className="story-action-btn"
                            style={{ color: `#87000C` }}
                            href={story.ctAs[0]?.[2]}
                            type="button"
                        >
                            {isLandscape ? "..." : story.ctAs[0]?.[1] || "SAZNAJ VIŠE"}
                            <svg width="13" height="13" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: "1rem" }}>
                                <path d="M9.44221 13.6233C9.18539 13.3722 9.06212 13.0688 9.07239 12.713C9.08352 12.3572 9.2175 12.0538 9.47432 11.8027L13.1019 8.2556H1.78411C1.42028 8.2556 1.11509 8.13507 0.86854 7.89399C0.622847 7.65375 0.5 7.35575 0.5 7C0.5 6.64424 0.622847 6.34583 0.86854 6.10475C1.11509 5.86451 1.42028 5.74439 1.78411 5.74439H13.1019L9.44221 2.16592C9.18539 1.9148 9.05698 1.61638 9.05698 1.27067C9.05698 0.925799 9.18539 0.627802 9.44221 0.376681C9.69904 0.12556 10.0042 0 10.3578 0C10.7105 0 11.0152 0.12556 11.2721 0.376681L17.1469 6.12108C17.2753 6.24664 17.3665 6.38266 17.4204 6.52915C17.4735 6.67563 17.5 6.83259 17.5 7C17.5 7.16741 17.4735 7.32436 17.4204 7.47085C17.3665 7.61734 17.2753 7.75336 17.1469 7.87892L11.24 13.6547C11.0045 13.8849 10.7105 14 10.3578 14C10.0042 14 9.69904 13.8744 9.44221 13.6233Z" fill="#87000C" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        )
    }

    // ========== RENDER COMPONENT ==========

    return (
        <>
            <MetaTags title="SuperStory" description="SuperPriče u SuperSport CASINU!" />
            <Header key="header" {...props}></Header>
            <ObavijestiCasino {...props}></ObavijestiCasino>
            <div className="super-story-page">
                {isLandscape ?
                    <div className="landscape-mask">
                        <div className="landscape-mask-image"></div>
                        <p className="landscape-orientation-message">Okreni ekran za bolji doživljaj</p>
                    </div>
                    : null}
                <div className="dummy-spacer"></div>
                <div className="outer-container" style={{ height: `${screenHeight - 60}px`, maxHeight: `${screenHeight - 60}px` }}>
                    {storyData.length > 0 &&
                        <div className="story-controls">
                            <div className="previous-story" onClick={goToPrevious}></div>
                            <div className="next-story" onClick={goToNext}></div>
                        </div>
                    }

                    {/* LOADER ANIMATION USING RIVE */}
                    {storyData.length > 0 &&
                        <div
                            className="buffering-indicator"
                            style={{ opacity: isPlaying ? 0 : 1, transition: 'opacity 0.3s' }}
                        >
                            <div className="rive-container">
                                <RiveComponent
                                    lazyLoad={true}
                                    cacheBust={`c=${casino.config.guiCacheVerzija}`}
                                    src="Content/Rive/ss_slogo_loader.riv"
                                    stateMachines="State Machine 1"
                                    artboard="Artboard"
                                    useOffscreenRenderer={true}
                                    playRequest={true}
                                    disablePlayOutsideViewport={true}
                                />
                            </div>
                        </div>
                    }

                    {/* TOP NAVIGATION BAR */}
                    {storyData.length > 0 && !isLandscape ?
                        <div className="top-container" style={{ width: videoWidth ? `${videoWidth}px` : '100%' }}>
                            {<div className="navigation-bar">
                                {storyData && storyData.map((story, i) => (
                                    <div
                                        key={i}
                                        className="story-bar"
                                        style={{
                                            width: `${92.8 / storyData.length}vw`,
                                        }}
                                    >
                                        {i < activeIndex && (
                                            <div className="story-bar-complete" style={{ width: '100%' }}></div>
                                        )}
                                        {i === activeIndex && (
                                            <div
                                                className="story-bar-complete"
                                                style={{
                                                    width: `${videoProgress * 100}%`,
                                                    transition: 'width 0.1s linear',
                                                }}
                                            ></div>
                                        )}
                                    </div>
                                ))}
                            </div>}
                            <NavLink to={props.routePrefix || '' + '/'} className="close-btn">
                                <span className="x-icon"></span>
                            </NavLink>
                        </div>
                        : null
                    }
                    {/* RENDER ALL STORY ITEMS */}
                    {storyData && storyData.map((story, i) => renderStory(story, i))}

                    {/* NO STORIES SCREEN */}
                    {storyData.length === 0 && showNoMoreStories &&
                        <div className="no-more-stories">
                            <NavLink to={props.routePrefix || '' + '/'} className="close-btn-black">
                                <div className="x-icon-black"></div>
                            </NavLink>
                            <div className="hourglass"></div>
                            <div className="message">Sve je pregledano. Prati nas i budi u toku s novim pričama.</div>
                        </div>
                    }
                </div>
            </div>
        </>
    )
}